import React, { useEffect, useState } from "react";
import { message, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import md5 from 'js-md5';
import axios from "axios";
const { Dragger } = Upload;

// Constants
const MAX_FILE_SIZE_MB = 50;
const MAX_CHUNK_RETRIES = 3;
const CHUNK_SIZE = 1024 * 1024; // 1 MB

const DraggerBox = ({ mode, categoryId }) => {

    const [mode_url, set_mode_url] = useState("/document/document");

    useEffect(() => {
        if (mode === "assetfiles") {
            set_mode_url("/assetfile/assetfile");
        }
    }, []);


    const readFileAsArrayBuffer = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                resolve(event.target.result);
            };

            reader.onerror = (event) => {
                reject(event.target.error);
            };

            reader.readAsArrayBuffer(file);
        });
    };

    const calculateMD5Hash = async (file) => {
        const totalSize = file.size;
        const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);
        let hash = md5.create();
        for (let chunk = 0; chunk < totalChunks; chunk++) {
            const start = chunk * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, totalSize);
            const chunkData = file.slice(start, end);

            const response = await readFileAsArrayBuffer(chunkData);
            hash.update(response)
        }
        return hash.hex();
    };

    // Upload chunk with retry logic
    const uploadChunkWithRetry = async (url, formData, retryCount = 0) => {
        try {
            const response = await axios.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response;
        } catch (error) {
            if (retryCount < MAX_CHUNK_RETRIES) {
                const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
                await new Promise(resolve => setTimeout(resolve, delay));
                return uploadChunkWithRetry(url, formData, retryCount + 1);
            }
            throw error;
        }
    };

    // const calculateFileMD5 = (file) => {
    //     return new Promise((resolve, reject) => {
    //         const reader = new FileReader();

    //         reader.onload = (event) => {
    //             const fileData = event.target.result;
    //             const crypto = window.crypto;
    //             if (crypto.subtle) {
    //                 crypto.subtle.digest("MD5", new TextEncoder().encode(fileData))
    //                     .then((hashBuffer) => {
    //                         const hashArray = Array.from(new Uint8Array(hashBuffer));
    //                         const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
    //                         resolve(hashHex);
    //                     })
    //                     .catch(reject);
    //             } else {
    //                 // Handle older browsers that do not support the Web Crypto API
    //                 reject(new Error("Web Crypto API not supported"));
    //             }
    //         };

    //         reader.onerror = reject;

    //         reader.readAsArrayBuffer(file);
    //     });
    // };

    const upload_file = async ({ file, onSuccess, onError, onProgress }) => {
        const hash = await calculateMD5Hash(file);

        try {
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

            let fileId = null;
            const lastModified = Date.now();
            const empty_blob_file = new File([], file.name, { lastModified });

            const formData = new FormData();
            formData.set('file', empty_blob_file);
            if (categoryId) {
                formData.set('category_id', categoryId);
            }
            const response = await axios.post(process.env.REACT_APP_BASE_URL + mode_url + "/upload_document/", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 200) {
                fileId = response.data.id;
            }
            formData.delete('file');

            let uploadedSize = 0;
            const totalSize = file.size;

            for (let chunk = 0; chunk < totalChunks; chunk++) {
                const start = chunk * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, totalSize);
                const chunkData = file.slice(start, end);
                uploadedSize += (end - start);

                formData.set('chunk', chunkData);
                formData.set('status', chunk === 0 ? 'upload_started' : 'uploading');
                formData.set('last_chunk', chunk === (totalChunks - 1));

                if (fileId != null) {
                    formData.set('id', fileId);
                }
                if (categoryId) {
                    formData.set('category_id', categoryId);
                }

                if (chunk === (totalChunks - 1)) {
                    formData.set('client_hash', hash);
                }

                try {
                    const chunkResponse = await uploadChunkWithRetry(
                        process.env.REACT_APP_BASE_URL + mode_url + "/" + fileId + "/receive_and_merge_chunks/",
                        formData
                    );

                    if (chunkResponse.status === 200) {
                        if (chunk === (totalChunks - 1)) {
                            onSuccess('File uploaded successfully', file);
                        } else {
                            const percent = Math.min((uploadedSize / totalSize) * 100, 99);
                            onProgress({ percent });
                        }
                    } else {
                        onError("Error uploading chunk");
                        return;
                    }
                } catch (error) {
                    onError(error);
                    return;
                }

                formData.delete('chunk');
            }

        } catch (e) {
            onError(e);
        }
    }

    const props = {
        name: 'file',
        multiple: true,
        progress: {
            strokeColor: {
                '0%': '#108ee9',
                '100%': '#87d068',
            },
            strokeWidth: 3,
            format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
        },
        beforeUpload: (file) => {
            const fileSizeMB = file.size / 1024 / 1024;
            if (fileSizeMB > MAX_FILE_SIZE_MB) {
                message.error(`File "${file.name}" is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
                return Upload.LIST_IGNORE;
            }
            return true;
        },
        customRequest: async ({ file, onSuccess, onError, onProgress }) => await upload_file({ file, onSuccess, onError, onProgress }),
        onChange(info) {
            const { status } = info.file;
            if (status === 'done') {
                message.success(`${info.file.name} file uploaded successfully.`);
            } else if (status === 'error') {
                message.error(`${info.file.name} file upload failed.`);
            }
        },
    };

    return (
        <div>
            <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">
                    Support for a single or bulk upload. Strictly prohibit from uploading company data or other
                    band files
                </p>
            </Dragger>
        </div>

    );
}

export default DraggerBox;